import { useEffect } from 'react';
import { noop } from 'utils/noop';
import { ESCAPE_KEY, TAB_KEY } from 'constants/keyCodes';

// Key down event handler used by focus trap hook below
const onKeyDownFocusTrapHandler = (
  e: KeyboardEvent,
  onClose: () => void,
  firstFocusableElement?: HTMLElement,
  lastFocusableElement?: HTMLElement,
) => {
  if (e.keyCode === ESCAPE_KEY) {
    onClose();
  }

  const isTabPressed = e.key === 'Tab' || e.keyCode === TAB_KEY;

  if (!isTabPressed) {
    return;
  }

  let focusableElement = null;

  if (e.shiftKey) {
    // if shift key pressed for shift + tab combination
    if (document.activeElement === firstFocusableElement) {
      focusableElement = lastFocusableElement;
    }
  } else if (document.activeElement === lastFocusableElement) {
    focusableElement = firstFocusableElement;
  }

  if (focusableElement) {
    focusableElement.focus(); // add focus for the last focusable element
    e.preventDefault();
  }
};

/**
 * Focus trap hook for dialogs/modals
 *
 * Adapted from https://uxdesign.cc/how-to-trap-focus-inside-modal-to-make-it-ada-compliant-6a50f9a70700
 * occasionally we don't want the focustrap enabled, for instance,
 * if the element we want to focus trap hasn't loaded onto the screen yet
 */
export const useFocusTrap = (
  onClose: () => void,
  componentRef: React.RefObject<HTMLElement | null>,
  isDisabled = false,
  dependencyArray: any[] = [],
) => {
  const focusableElements =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  useEffect(() => {
    if (isDisabled) {
      // we need to return a no-op function, because the non-disabled
      // state expects a function return.
      return noop;
    }

    const focusableContent = componentRef.current?.querySelectorAll<HTMLElement>(focusableElements);

    const firstFocusableElement = focusableContent?.[0]; // get first element to be focused inside component
    const lastFocusableElement = focusableContent?.[focusableContent.length - 1]; // get last element to be focused inside component
    const onKeyDownHandler = (e: KeyboardEvent) => {
      onKeyDownFocusTrapHandler(e, onClose, firstFocusableElement, lastFocusableElement);
    };

    document.addEventListener('keydown', onKeyDownHandler);

    return () => document.removeEventListener('keydown', onKeyDownHandler);
  }, [componentRef, isDisabled, onClose, ...dependencyArray]);
};
